import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth";
import api from "../api";
import { Waves, MapPin, LayoutDashboard, LogOut, UserCircle2, ArrowLeft, Bell, Heart, MessageCircle, ShoppingCart, HelpCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import MobileBottomNav from "./MobileBottomNav";
import CookieBanner from "./CookieBanner";
import BoostedBanner from "./BoostedBanner";
import LegalReacceptBanner from "./LegalReacceptBanner";
import { toast } from "sonner";

/** Handles the PWA `beforeinstallprompt` event so we can trigger the
 *  native "Add to home screen" dialog when the footer logo is clicked. */
function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  useEffect(() => {
    const onBefore = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => setDeferredPrompt(null);
    const onTrigger = async () => {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        try { await deferredPrompt.userChoice; } catch { /* ignore */ }
        setDeferredPrompt(null);
      } else if (/iPad|iPhone|iPod/i.test(navigator.userAgent)) {
        toast.info("Sur iPhone : appuyez sur Partager → Sur l'écran d'accueil pour installer l'app.");
      } else {
        toast.info("Pour installer : ouvrez le menu de votre navigateur → Installer MySwimmingLessons.");
      }
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("pwa:install", onTrigger);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("pwa:install", onTrigger);
    };
  }, [deferredPrompt]);
}

function CartIcon() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const reload = () => api.get("/cart").then((r) => setCount(r.data.items?.length || 0)).catch(() => {});
    reload();
    const id = setInterval(reload, 8000);
    const onFocus = () => reload();
    window.addEventListener("cart:updated", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("cart:updated", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return (
    <Link to="/cart" data-testid="nav-cart">
      <Button variant="ghost" size="icon" className="rounded-full relative">
        <ShoppingCart className="w-5 h-5" />
        {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-sky-700 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-badge">{count}</span>}
      </Button>
    </Link>
  );
}

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();
  const reload = () => api.get("/notifications", { params: { limit: 20 } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    reload();
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, []);
  const unread = items.filter((n) => !n.read).length;
  const click = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.notification_id}/read`); }
    catch (e) { console.warn("Notif: échec mark-read", n.notification_id, e?.response?.status); }
    reload();
    nav(notificationTarget(n, user?.role));
  };
  const markAll = async (e) => {
    e?.stopPropagation?.();
    try { await api.post("/notifications/mark-all-read"); reload(); }
    catch (err) { console.warn("Notif: mark-all-read failed", err); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b flex items-center justify-between gap-2">
          <span className="font-semibold" style={{ fontFamily: "Outfit" }}>Notifications</span>
          {unread > 0 && (
            <button
              onClick={markAll}
              className="text-xs text-sky-700 hover:underline font-medium"
              data-testid="notif-mark-all-read"
            >
              Tout marquer comme lu
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">Aucune notification</div>}
          {items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => click(n)}
              className={`w-full text-left p-3 border-b hover:bg-sky-50 transition ${!n.read ? "bg-sky-50/40" : ""}`}
              data-testid={`notif-item-${n.notification_id}`}
            >
              <div className="text-sm font-semibold text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600">{n.body}</div>
              <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
            </button>
          ))}
          {items.length >= 20 && (
            <Link to="/notifications" className="block p-3 text-center text-xs text-sky-700 hover:underline border-t" data-testid="notif-see-all">
              Voir toutes les notifications →
            </Link>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MessagesIcon() {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const reload = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
    reload();
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <Link to="/messages" data-testid="nav-messages">
      <Button variant="ghost" size="icon" className="rounded-full relative">
        <MessageCircle className="w-5 h-5" />
        {unread > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unread}</span>}
      </Button>
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const showBack = location.pathname !== "/";
  const dashLink = user?.role === "mns" ? "/mns/dashboard" : user?.role === "admin" ? "/admin" : "/dashboard";
  usePwaInstall();

  return (
    <div className="min-h-screen flex flex-col water-bg">
      <LegalReacceptBanner />
      <header className="sticky top-0 z-40 glass border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {showBack && (
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => nav(-1)} data-testid="back-btn" aria-label="Retour">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <Link to="/" className="flex items-center gap-2 font-bold text-sky-800" data-testid="brand-logo">
              <img src="/msl-logo.png" alt="MySwimmingLessons" className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg object-contain bg-white" />
              <span className="text-lg sm:text-xl hidden sm:inline" style={{ fontFamily: "Outfit" }}>MySwimmingLessons</span>
            </Link>
          </div>
          <nav className="flex items-center gap-1.5">
            <Link to="/map" data-testid="nav-map" className="hidden md:inline-flex">
              <Button variant="ghost" className="rounded-full"><MapPin className="w-4 h-4 mr-1" />Carte</Button>
            </Link>
            <Link to="/aide" data-testid="nav-help" className="hidden md:inline-flex" title="Centre d'aide & FAQ">
              <Button variant="ghost" size="icon" className="rounded-full" aria-label="Aide"><HelpCircle className="w-5 h-5" /></Button>
            </Link>
            {user ? (
              <>
                <Link to={dashLink} data-testid="nav-dashboard" className="hidden md:inline-flex">
                  <Button variant="ghost" className="rounded-full"><LayoutDashboard className="w-4 h-4 mr-1" />Tableau de bord</Button>
                </Link>
                {user.role === "client" && (
                  <>
                    <CartIcon />
                    <Link to="/favorites" data-testid="nav-favs" className="hidden md:inline-flex"><Button variant="ghost" size="icon" className="rounded-full"><Heart className="w-5 h-5" /></Button></Link>
                  </>
                )}
                <div className="hidden md:inline-flex"><NotificationsBell /></div>
                <div className="hidden md:inline-flex"><MessagesIcon /></div>
                <Link to="/profile" data-testid="nav-profile" className="hidden md:inline-flex">
                  <Button variant="ghost" className="rounded-full">
                    <UserCircle2 className="w-4 h-4 mr-1" /> {user.name?.split(" ")[0] || "Profil"}
                    <span className="text-xs uppercase tracking-wide bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full ml-2">{user.role}</span>
                  </Button>
                </Link>
                <Button variant="outline" className="rounded-full hidden md:inline-flex" onClick={async () => { await logout(); nav("/"); }} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Déconnexion</span>
                </Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login">Connexion</Button></Link>
                <Link to="/register"><Button className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="nav-register">Créer un compte</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>
      {/* Iter35 wave 3: scrolling boost banner — only on client-facing pages */}
      {user?.role === "client" && <BoostedBanner />}
      <main className="flex-1 pb-20 md:pb-0">{children}</main>
      <MobileBottomNav />
      <CookieBanner />
      <footer className="py-8 text-center text-sm text-slate-500 border-t border-sky-100/60 bg-white/40 mt-12">
        <div className="max-w-5xl mx-auto px-4">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("pwa:install"))}
            className="inline-flex flex-col items-center gap-2 group hover:opacity-80 transition mb-4"
            title="Installer l'application sur votre téléphone"
            data-testid="footer-pwa-install"
          >
            <img src="/msl-logo.png" alt="MySwimmingLessons" className="h-20 w-20 object-contain" />
            <span className="text-xs text-sky-700 group-hover:underline font-medium">Installer l'application</span>
          </button>
          <div className="flex justify-center gap-4 mb-2 text-xs flex-wrap">
            <Link to="/avis" className="hover:text-sky-700" data-testid="footer-avis">Avis clients</Link>
            <Link to="/aide" className="hover:text-sky-700" data-testid="footer-aide">Aide & FAQ</Link>
            <Link to="/contact" className="hover:text-sky-700" data-testid="footer-contact">Nous contacter</Link>
            <Link to="/legal/cgu" className="hover:text-sky-700">CGU</Link>
            <Link to="/legal/cgv" className="hover:text-sky-700">CGV</Link>
            <Link to="/legal/privacy" className="hover:text-sky-700">Politique de confidentialité</Link>
            <Link to="/legal/mentions" className="hover:text-sky-700">Mentions légales</Link>
            <button onClick={() => window.dispatchEvent(new CustomEvent("cookies:reopen"))} className="hover:text-sky-700 underline-offset-2 hover:underline" data-testid="footer-cookies">Gérer les cookies</button>
          </div>
          <div>Vos données personnelles sont utilisées pour gérer vos réservations et améliorer nos services.</div>
          © {new Date().getFullYear()} MySwimmingLessons — Réservez vos cours de natation
        </div>
      </footer>
    </div>
  );
}
